import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faKey,
  faBan,
  faCheckCircle,
  faRightFromBracket,
  faLink,
  faAward,
  faBookOpen,
  faStar,
  faFire,
  faRefresh,
} from '@fortawesome/free-solid-svg-icons';
import { streetlearnApi } from '../api';
import { showToast } from '../../components/toast';
import GrantModal from './GrantModal';
import '../streetlearn.css';

const LearnerDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('curriculum');

  // Modals & Actions
  const [showGrantModal, setShowGrantModal] = useState(false);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [selectedEntitlementId, setSelectedEntitlementId] = useState(null);
  const [revokeNote, setRevokeNote] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  // Edit Note
  const [isEditingNote, setIsEditingNote] = useState(false);
  const [staffNote, setStaffNote] = useState('');

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await streetlearnApi.getLearnerById(id);
      setData(res);
      setStaffNote(res.learner?.staff_note || '');
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to load learner details');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleToggleStatus = async () => {
    const current = data.learner.status;
    const nextStatus = current === 'active' ? 'blocked' : 'active';
    const confirmMsg =
      nextStatus === 'blocked'
        ? 'Are you sure you want to block this learner? They will be unable to log in to StreetLearn.'
        : 'Unblock this learner?';

    if (!window.confirm(confirmMsg)) return;

    try {
      setActionLoading(true);
      await streetlearnApi.updateLearnerStatus(id, { status: nextStatus });
      showToast.success(`Learner status updated to ${nextStatus}`);
      loadData();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleInvalidateSessions = async () => {
    if (!window.confirm('Force logout this learner from all active mobile & web sessions?')) return;
    try {
      setActionLoading(true);
      await streetlearnApi.invalidateLearnerSessions(id);
      showToast.success('All active learner sessions invalidated');
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to invalidate sessions');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveStaffNote = async () => {
    try {
      setActionLoading(true);
      await streetlearnApi.updateLearnerStaffNote(id, { staff_note: staffNote.trim() });
      showToast.success('Staff note saved');
      setIsEditingNote(false);
      loadData();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to save staff note');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRevokeEntitlement = async () => {
    if (!revokeNote.trim()) {
      showToast.error('Please enter a note explaining why access is revoked');
      return;
    }
    try {
      setActionLoading(true);
      await streetlearnApi.revokeEntitlement(selectedEntitlementId, {
        note: revokeNote.trim(),
      });
      showToast.success('Access entitlement revoked');
      setShowRevokeModal(false);
      setRevokeNote('');
      loadData();
    } catch (err) {
      showToast.error(err.response?.data?.message || 'Failed to revoke entitlement');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-5 text-center py-5">
        <FontAwesomeIcon icon={faRefresh} spin size="2x" className="text-primary mb-2" />
        <p className="text-muted">Loading learner 360° profile...</p>
      </div>
    );
  }

  if (!data?.learner) {
    return (
      <div className="mt-5 text-center py-5">
        <h4>Learner not found</h4>
        <button
          className="btn btn-default btn-sm mt-2"
          onClick={() => navigate('/streetlearn/learners')}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back to Learners
        </button>
      </div>
    );
  }

  const { learner, state, levels, entitlements, orders, certificates, crmSubscriber } = data;
  const isEntitled = entitlements?.some((e) => !e.revoked_at);

  return (
    <div className="mt-5 streetlearn-container">
      {/* Header Row */}
      <div className="row pb-2 align-items-center">
        <div className="col-md-6">
          <button
            className="btn btn-default btn-sm"
            onClick={() => navigate('/streetlearn/learners')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-1" /> Back to Learners
          </button>
        </div>
        <div className="col-md-6 text-right">
          {!isEntitled ? (
            <button
              className="btn btn-success btn-sm mx-1"
              onClick={() => setShowGrantModal(true)}
              disabled={actionLoading}
            >
              <FontAwesomeIcon icon={faKey} /> Grant Lifetime Access
            </button>
          ) : (
            <span className="badge badge-primary py-2 px-3 mx-1" style={{ fontSize: '12px' }}>
              Lifetime Access Active
            </span>
          )}

          <button
            className={`btn btn-sm mx-1 ${
              learner.status === 'active' ? 'btn-danger' : 'btn-default'
            }`}
            onClick={handleToggleStatus}
            disabled={actionLoading}
          >
            <FontAwesomeIcon icon={learner.status === 'active' ? faBan : faCheckCircle} />{' '}
            {learner.status === 'active' ? 'Block Learner' : 'Unblock Learner'}
          </button>

          <button
            className="btn btn-default btn-sm mx-1"
            title="Force logout all devices"
            onClick={handleInvalidateSessions}
            disabled={actionLoading}
          >
            <FontAwesomeIcon icon={faRightFromBracket} /> Force Logout
          </button>
        </div>
      </div>

      {/* Learner Profile Panel */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body">
          <div className="row align-items-center">
            <div className="col-md-8">
              <div>
                <h4 className="m-0 font-bold text-dark d-flex align-items-center flex-wrap gap-2">
                  <span>{learner.name || 'Learner'}</span>
                  <span className="text-muted" style={{ fontSize: '13px', fontWeight: 600 }}>#{learner.id}</span>
                  {learner.status === 'active' ? (
                    <span className="text-success font-weight-bold" style={{ fontSize: '12px' }}>Active</span>
                  ) : (
                    <span className="text-danger font-weight-bold" style={{ fontSize: '12px' }}>Blocked</span>
                  )}
                </h4>
                <div
                  className="text-muted d-flex gap-3 flex-wrap mt-2"
                  style={{ fontSize: '13px' }}
                >
                  <span>
                    📱 <strong>{learner.phone}</strong>
                  </span>
                  {learner.email && (
                    <span>
                      ✉️ <strong>{learner.email}</strong>
                    </span>
                  )}
                  <span>
                    📅 Registered: {new Date(learner.created_at).toLocaleDateString()}
                  </span>
                  <span>
                    🕒 Last Login:{' '}
                    {learner.last_login_at
                      ? new Date(learner.last_login_at).toLocaleString()
                      : 'Never'}
                  </span>
                </div>
              </div>
            </div>

            <div className="col-md-4 text-right">
              {crmSubscriber ? (
                <div className="text-muted" style={{ fontSize: '13px' }}>
                  <FontAwesomeIcon icon={faLink} className="text-primary me-1" />
                  Linked Sub: <strong className="text-dark">#{crmSubscriber.id} ({crmSubscriber.subscriberid})</strong>
                </div>
              ) : (
                <div className="text-muted" style={{ fontSize: '13px' }}>
                  Not Linked to CRM
                </div>
              )}
            </div>
          </div>

          {/* Internal Staff Notes */}
          <div className="mt-3 pt-3 border-top">
            <div className="d-flex justify-content-between align-items-center">
              <strong style={{ fontSize: '13px' }} className="text-muted">
                Staff Private Note:
              </strong>
              {!isEditingNote && (
                <button
                  className="btn btn-default btn-xs"
                  onClick={() => setIsEditingNote(true)}
                >
                  Edit Note
                </button>
              )}
            </div>
            {isEditingNote ? (
              <div className="mt-2">
                <textarea
                  className="form-control mb-2"
                  rows="2"
                  value={staffNote}
                  onChange={(e) => setStaffNote(e.target.value)}
                  placeholder="Enter private staff note for this learner..."
                />
                <div className="text-right">
                  <button
                    className="btn btn-default btn-xs me-2"
                    onClick={() => setIsEditingNote(false)}
                  >
                    Cancel
                  </button>
                  <button
                    className="btn btn-primary btn-xs"
                    onClick={handleSaveStaffNote}
                    disabled={actionLoading}
                  >
                    Save Note
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-muted small mb-0 mt-1">
                {learner.staff_note || 'No internal staff note recorded.'}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Metrics Row (4 Native Color Admin Widgets) */}
      <div className="row mb-3">
        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-dark-blue inverse-mode"
            style={{
              background: 'linear-gradient(135deg, #1b355a 0%, #28548a 100%)',
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Lessons Completed
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {levels?.reduce((sum, l) => sum + l.lessons_done, 0)}{' '}
              <span style={{ fontSize: '15px', fontWeight: 'normal', opacity: 0.85 }}>/ 147</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-green inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Exams Passed
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {levels?.filter((l) => l.exam_passed).length}{' '}
              <span style={{ fontSize: '15px', fontWeight: 'normal', opacity: 0.85 }}>/ 16</span>
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-orange inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Tier Certificates
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '32px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {certificates?.length || 0}
            </div>
          </div>
        </div>

        <div className="col-xl-3 col-lg-3 col-md-6 col-sm-6 col-6 mb-3">
          <div
            className="widget widget-stats bg-gradient-purple inverse-mode"
            style={{
              minHeight: '96px',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              padding: '12px 14px',
              borderRadius: '6px',
              marginBottom: '0',
            }}
          >
            <div
              className="widget-stats-title"
              style={{
                fontSize: '15px',
                fontWeight: '600',
                color: 'rgba(255,255,255,0.95)',
                lineHeight: '1.2',
                textAlign: 'left',
              }}
            >
              Streak & XP
            </div>
            <div
              className="widget-stats-value"
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                lineHeight: '1',
                textAlign: 'right',
              }}
            >
              {state?.streak_count || 0}d &bull; {state?.xp || 0} XP
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="panel panel-default with-rounded-corner with-shadow mb-3">
        <div className="panel-body py-2">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'curriculum' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('curriculum')}
            >
              Level Progress ({levels?.length || 0})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'entitlements' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('entitlements')}
            >
              Access & Entitlements ({entitlements?.length || 0})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'orders' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('orders')}
            >
              Orders & Payments ({orders?.length || 0})
            </button>
            <button
              type="button"
              className={`btn btn-sm ${
                activeTab === 'certificates' ? 'btn-primary active' : 'btn-default'
              }`}
              onClick={() => setActiveTab('certificates')}
            >
              Certificates ({certificates?.length || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Tab 1: Curriculum Progress */}
      {activeTab === 'curriculum' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">16-Level Curriculum Mastery & Exam Passes</h4>
          </div>
          <div className="panel-body">
            <div className="row">
              {levels?.map((lvl) => {
                const pct =
                  lvl.lessons_total > 0
                    ? Math.round((lvl.lessons_done / lvl.lessons_total) * 100)
                    : 0;
                return (
                  <div className="col-md-4 col-sm-6 mb-3" key={lvl.id}>
                    <div className="panel panel-default with-rounded-corner with-shadow h-100 p-3">
                      <div className="d-flex justify-content-between align-items-start mb-2">
                        <strong className="text-dark">
                          Level {lvl.number}: {lvl.title}
                        </strong>
                        {lvl.exam_passed ? (
                          <span className="badge badge-success">Passed</span>
                        ) : lvl.exam_attempts > 0 ? (
                          <span className="badge badge-warning">Exam Incomplete</span>
                        ) : (
                          <span className="badge badge-default">Not Started</span>
                        )}
                      </div>

                      <div className="small text-muted mb-1 d-flex justify-content-between">
                        <span>Lessons Completed:</span>
                        <strong>
                          {lvl.lessons_done} / {lvl.lessons_total}
                        </strong>
                      </div>
                      <div className="progress progress-striped mb-2" style={{ height: '8px' }}>
                        <div
                          className={`progress-bar ${
                            pct === 100 ? 'progress-bar-success' : 'progress-bar-primary'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>

                      <div className="small text-muted d-flex justify-content-between">
                        <span>
                          Exam Attempts: <strong>{lvl.exam_attempts}</strong>
                        </span>
                        <span>
                          Best Score:{' '}
                          <strong>{lvl.exam_best !== null ? `${lvl.exam_best}%` : '-'}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Entitlements */}
      {activeTab === 'entitlements' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Lifetime Entitlements & Grants</h4>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Product</th>
                    <th>Source</th>
                    <th>Audit Note</th>
                    <th>Granted At</th>
                    <th>Status</th>
                    <th style={{ textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {entitlements?.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No entitlements granted to this learner.
                      </td>
                    </tr>
                  ) : (
                    entitlements?.map((e) => (
                      <tr key={e.id}>
                        <td>#{e.id}</td>
                        <td className="font-bold text-capitalize">{e.product}</td>
                        <td>
                          <span
                            className={`badge ${
                              e.source === 'payment' ? 'badge-info' : 'badge-success'
                            }`}
                          >
                            {e.source}
                          </span>
                        </td>
                        <td>{e.note || '-'}</td>
                        <td className="small text-muted">
                          {new Date(e.granted_at).toLocaleString()}
                        </td>
                        <td>
                          {!e.revoked_at ? (
                            <span className="badge badge-success">Active</span>
                          ) : (
                            <div>
                              <span className="badge badge-danger">Revoked</span>
                              <div className="small text-muted">{e.revoke_note}</div>
                            </div>
                          )}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {!e.revoked_at && (
                            <button
                              className="btn btn-danger btn-xs"
                              onClick={() => {
                                setSelectedEntitlementId(e.id);
                                setShowRevokeModal(true);
                              }}
                            >
                              Revoke Access
                            </button>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Orders */}
      {activeTab === 'orders' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Order & Payment Transactions</h4>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Order ID</th>
                    <th>Product</th>
                    <th>Amount</th>
                    <th>Status</th>
                    <th>Razorpay IDs</th>
                    <th>Paid At</th>
                  </tr>
                </thead>
                <tbody>
                  {orders?.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        No orders recorded for this learner.
                      </td>
                    </tr>
                  ) : (
                    orders?.map((o) => (
                      <tr key={o.id}>
                        <td className="font-bold">#{o.id}</td>
                        <td>{o.product}</td>
                        <td className="font-bold text-primary">
                          ₹{(o.amount_paise / 100).toFixed(2)}
                        </td>
                        <td>
                          <span
                            className={`badge ${
                              o.status === 'paid'
                                ? 'badge-success'
                                : o.status === 'refunded'
                                ? 'badge-warning'
                                : 'badge-default'
                            }`}
                          >
                            {o.status}
                          </span>
                        </td>
                        <td className="small">
                          <div>Order: {o.razorpay_order_id}</div>
                          {o.razorpay_payment_id && <div>Payment: {o.razorpay_payment_id}</div>}
                        </td>
                        <td className="small text-muted">
                          {o.paid_at ? new Date(o.paid_at).toLocaleString() : '-'}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab 4: Certificates */}
      {activeTab === 'certificates' && (
        <div className="panel panel-default with-rounded-corner with-shadow">
          <div className="panel-heading border-0">
            <h4 className="panel-title m-0">Issued Tier Certificates</h4>
          </div>
          <div className="panel-body p-0">
            <div className="table-responsive">
              <table className="table table-striped table-bordered table-hover align-middle mb-0">
                <thead>
                  <tr>
                    <th>Serial #</th>
                    <th>Tier</th>
                    <th>Name on Certificate</th>
                    <th>Issued At</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {certificates?.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-4 text-muted">
                        No tier certificates earned yet.
                      </td>
                    </tr>
                  ) : (
                    certificates?.map((c) => (
                      <tr key={c.id}>
                        <td className="font-bold text-primary">
                          <FontAwesomeIcon icon={faAward} className="me-1" />
                          {c.serial}
                        </td>
                        <td>Tier {c.tier_id}</td>
                        <td className="font-bold">{c.name_on_cert}</td>
                        <td className="small text-muted">
                          {new Date(c.issued_at).toLocaleString()}
                        </td>
                        <td>
                          {!c.revoked_at ? (
                            <span className="badge badge-success">Valid</span>
                          ) : (
                            <span className="badge badge-danger">Revoked: {c.revoke_note}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Grant Modal */}
      {selectedEntitlementId === null && (
        <GrantModal
          show={showGrantModal}
          learner={learner}
          onHide={() => setShowGrantModal(false)}
          onSuccess={loadData}
        />
      )}

      {/* Revoke Modal */}
      {showRevokeModal && (
        <div
          className="modal show d-block"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }}
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header d-flex justify-content-between align-items-center">
                <h4 className="modal-title font-bold m-0">Revoke Lifetime Access</h4>
                <button
                  type="button"
                  className="close"
                  style={{ border: 'none', background: 'transparent', fontSize: '24px', lineHeight: '1', cursor: 'pointer', padding: '0 5px' }}
                  onClick={() => setShowRevokeModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">
                  Are you sure you want to revoke this entitlement? The learner will immediately
                  lose access to paid levels.
                </p>
                <div className="form-group mb-3">
                  <label className="font-semibold text-sm">
                    Reason for Revocation <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="e.g. Refund processed / Account violation / Mistaken grant"
                    value={revokeNote}
                    onChange={(e) => setRevokeNote(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-default btn-sm"
                  onClick={() => setShowRevokeModal(false)}
                  disabled={actionLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-sm"
                  onClick={handleRevokeEntitlement}
                  disabled={actionLoading}
                >
                  {actionLoading ? 'Revoking...' : 'Confirm Revoke'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LearnerDetailView;
